import React, { useState, useEffect, useRef } from 'react';
import './MentionAutocomplete.css';

/**
 * MentionAutocomplete component that provides @ mention functionality
 * Shows a dropdown of lists when user types @
 */
export default function MentionAutocomplete({
  value,
  onChange,
  onKeyDown,
  onBlur,
  lists = [],
  currentListId = null,
  inputRef = null,
  placeholder = '',
  className = '',
  rows = 1,
  disabled = false
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredLists, setFilteredLists] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Filter lists based on query, excluding current list
  useEffect(() => {
    if (showDropdown) {
      const filtered = lists
        .filter(list => list.id !== currentListId)
        .filter(list => {
          // If query is empty (user just typed @), show all lists
          const queryTrimmed = mentionQuery ? mentionQuery.trim() : '';
          if (queryTrimmed === '') {
            return true;
          }
          // Otherwise filter by name
          return list.name.toLowerCase().includes(mentionQuery.toLowerCase());
        });
      setFilteredLists(filtered);
      setSelectedIndex(0);
    } else {
      setFilteredLists([]);
    }
  }, [mentionQuery, lists, currentListId, showDropdown]);

  // Detect @ mentions in the text
  const detectMention = (text, cursorPosition) => {
    if (!text || cursorPosition < 0) {
      return null;
    }
    
    // Find the last @ before or at the cursor
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex === -1) {
      return null;
    }

    // Get text after @ up to cursor
    const afterAt = textBeforeCursor.substring(lastAtIndex + 1);
    
    // Check if there's a newline between @ and cursor (invalid mention)
    if (afterAt.includes('\n')) {
      return null;
    }

    // Check if @ is immediately followed by a space (invalid mention)
    if (afterAt.length > 0 && afterAt[0] === ' ') {
      return null;
    }

    // Get the query after @ - allow spaces for list names
    // Stop at newline or if we hit another @
    let queryEnd = afterAt.length;
    const newlineIndex = afterAt.indexOf('\n');
    const nextAtIndex = afterAt.indexOf('@', 1); // Start search after first char
    if (newlineIndex !== -1) {
      queryEnd = Math.min(queryEnd, newlineIndex);
    }
    if (nextAtIndex !== -1) {
      queryEnd = Math.min(queryEnd, nextAtIndex);
    }
    
    // Don't trim - preserve spaces for matching list names with spaces
    const query = afterAt.substring(0, queryEnd);
    
    // Return mention even if query is empty (user just typed @)
    return {
      start: lastAtIndex,
      query: query, // Can be empty string if user just typed @
      end: cursorPosition
    };
  };

  // Handle text change
  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart ?? newValue.length;
    
    // Call onChange first so parent can update state
    onChange(e);
    
    // Use requestAnimationFrame to check cursor position after DOM updates
    requestAnimationFrame(() => {
      // Re-read cursor position in case it changed
      const actualCursorPosition = inputRef?.current?.selectionStart ?? cursorPosition;
      
      // Check for @ mention
      let mention = detectMention(newValue, actualCursorPosition);
      
      // Fallback: if text ends with @ and cursor is at the end, show dropdown
      if (!mention && newValue.endsWith('@') && actualCursorPosition === newValue.length) {
        const lastAtIndex = newValue.lastIndexOf('@');
        if (lastAtIndex !== -1) {
          // Check if there's a space or newline before the @
          const beforeAt = newValue.substring(0, lastAtIndex);
          if (!beforeAt.endsWith(' ') && !beforeAt.endsWith('\n')) {
            mention = {
              start: lastAtIndex,
              query: '',
              end: actualCursorPosition
            };
          }
        }
      }
      
      if (mention) {
        setMentionStart(mention.start);
        setMentionQuery(mention.query);
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
        setMentionStart(null);
        setMentionQuery('');
      }
    });
  };

  // Insert mention into text
  const insertMention = (list) => {
    if (mentionStart === null || !inputRef?.current) return;
    
    const text = value;
    const beforeMention = text.substring(0, mentionStart);
    const afterMention = text.substring(mentionStart + 1 + mentionQuery.length);
    const newText = `${beforeMention}@${list.name}${afterMention}`;
    
    // Create synthetic event
    const syntheticEvent = {
      target: { value: newText },
      preventDefault: () => {}
    };
    
    onChange(syntheticEvent);
    
    // Set cursor position after the mention
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = mentionStart + list.name.length + 1; // +1 for @
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        inputRef.current.focus();
      }
    }, 0);
    
    setShowDropdown(false);
    setMentionStart(null);
    setMentionQuery('');
  };

  // Handle keyboard navigation
  const handleKeyDownWrapper = (e) => {
    if (showDropdown && filteredLists.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredLists.length - 1 ? prev + 1 : prev
        );
        return;
      }
      
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        return;
      }
      
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredLists[selectedIndex]);
        return;
      }
      
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        setMentionStart(null);
        setMentionQuery('');
        return;
      }
    }
    
    // Call original onKeyDown if provided
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Calculate dropdown position
  const getDropdownPosition = () => {
    if (!inputRef?.current || !containerRef.current) {
      return { top: 0, left: 0 };
    }
    
    const inputRect = inputRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    return {
      top: inputRect.bottom - containerRect.top + 4,
      left: inputRect.left - containerRect.left
    };
  };

  const InputComponent = rows > 1 ? 'textarea' : 'input';
  const inputProps = rows > 1 ? { rows } : {};

  const handleBlurWrapper = (e) => {
    // Don't close dropdown if clicking on dropdown item
    // Use setTimeout to allow click event to fire first
    setTimeout(() => {
      if (dropdownRef.current && !dropdownRef.current.contains(document.activeElement)) {
        setShowDropdown(false);
      }
    }, 200);
    // Call original onBlur if provided
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div ref={containerRef} className="mention-autocomplete-container">
      <InputComponent
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDownWrapper}
        onBlur={handleBlurWrapper}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        {...inputProps}
      />
      {showDropdown && filteredLists.length > 0 && (
        <div
          ref={dropdownRef}
          className="mention-autocomplete-dropdown"
          style={getDropdownPosition()}
        >
          {filteredLists.map((list, index) => (
            <div
              key={list.id}
              className={`mention-autocomplete-item ${
                index === selectedIndex ? 'mention-autocomplete-item-selected' : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                insertMention(list);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="mention-autocomplete-item-name">{list.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
